// Pure pricing math for a compatible-combination row, extracted from the CCB
// modal's renderTotalPrice (AHA-6681 CS-2) so the ducted cards can share it.
// The modal keeps its markup and delegates the math here.
import type {
  BasicProductData,
  EnrichedAccessory
} from 'components/layout/modals/compatible-combinations-browser-modal';

// Frontend copy of the backend `parseAccessoriesString`: pnet returns the
// per-combination Accessories column as a STRING_AGG of
// `accessoryID*systemAccessoryTypeID` pairs joined by commas (e.g.
// `"17*7,5*1"`). Null/empty when a combination has no required accessories.
export function parseAccessoriesString(
  raw: string | null | undefined
): Array<{ accessoryId: number; typeId: number }> {
  if (!raw) return [];
  return raw
    .split(',')
    .map((pair) => {
      const [accId, typeId] = pair.split('*').map(Number);
      return { accessoryId: accId, typeId };
    })
    .filter((p) => !Number.isNaN(p.accessoryId) && !Number.isNaN(p.typeId));
}

// 'total' = every real model (and resolvable accessory) priced; 'unavailable'
// = a real model or resolved accessory is missing product data or a price;
// 'none' = no real models present (e.g. a kept-only system with all Generic
// positions), so there is nothing to price.
export type CombinationPriceResult =
  | { kind: 'total'; total: number }
  | { kind: 'unavailable' }
  | { kind: 'none' };

export function computeCombinationTotalPrice(
  combo: Record<string, unknown>,
  productData: Record<string, BasicProductData>,
  accessoryData: Record<string, EnrichedAccessory>
): CombinationPriceResult {
  const realPositions = [
    {
      manufacturer: combo.OutdoorManufacturer,
      modelNumber: combo.OutdoorModelNumber
    },
    {
      manufacturer: combo.IndoorManufacturer,
      modelNumber: combo.IndoorModelNumber
    },
    {
      manufacturer: combo.BlowerManufacturer,
      modelNumber: combo.BlowerModelNumber
    }
  ].filter(
    (p) =>
      typeof p.manufacturer === 'string' &&
      typeof p.modelNumber === 'string' &&
      p.manufacturer !== 'Generic' &&
      p.modelNumber.length > 0
  );

  if (realPositions.length === 0) {
    return { kind: 'none' };
  }

  let total = 0;
  for (const pos of realPositions) {
    const product = productData[`${pos.manufacturer}|${pos.modelNumber}`];
    if (!product || product.price == null) {
      return { kind: 'unavailable' };
    }
    total += product.price;
  }
  // A real purchase takes ONE accessory per required TYPE, so the row shows
  // a "starting at" floor: components + the cheapest quotable option of each
  // type. Types with no resolved option (no AlpineProductLink) aren't
  // quotable — skip silently, as before. A type with resolved options but
  // none priceable is required-but-unpriceable → whole row unavailable.
  const perType = new Map<number, { resolved: boolean; min: number | null }>();
  for (const { accessoryId, typeId } of parseAccessoriesString(
    combo.Accessories as string | null | undefined
  )) {
    const group = perType.get(typeId) ?? { resolved: false, min: null };
    const entry = accessoryData[`${accessoryId}*${typeId}`];
    if (entry) {
      group.resolved = true;
      const price = entry.product?.price;
      if (price != null && (group.min == null || price < group.min)) {
        group.min = price;
      }
    }
    perType.set(typeId, group);
  }
  for (const group of perType.values()) {
    if (group.resolved) {
      if (group.min == null) return { kind: 'unavailable' };
      total += group.min;
    }
  }
  return { kind: 'total', total };
}
